import * as ts from 'io-ts'
import React, {useEffect, useState} from 'react'
import {v4} from 'uuid'
import {Button} from '../components/library/buttons'
import {Select} from '../components/library/form'
import {JSONField} from '../components/library/json-field'
import {usePersistedState} from '../hooks/use-persisted-state'
import {useStateChanged} from '../hooks/use-state-changed'
import {getApiUrl} from '../lib/api'
import {CUSTOMER_API_URL} from '../lib/env'

// from https://developer.salesforce.com/docs/marketing/marketing-cloud/guide/using-postmonger.html
const triggers = [
  'ready',
  'requestTokens',
  'requestEndpoints',
  'nextStep',
  'prevStep',
  'requestCulture',
  'updateSteps',
  'updateButton',
  'updateActivity',
  'destroy',
  'requestInteractionDefaults',
  'requestInteraction',
  'requestTriggerEventDefinition'
]

const events = [
  'initActivity',
  'requestedTokens',
  'requestedEndpoints',
  'clickedNext',
  'clickedBack',
  'gotoStep',
  'requestedCulture',
  'requestedInteractionDefaults',
  'requestedInteraction',
  'requestedTriggerEventDefinition'
]

// See config.jsonc for the baseline example. This is just so that the plugin can
// be tested locally or directly on hosted endpoint without the need for a SFMC host.
// The initActivity is the message that is sent from the SFMC host to the plugin to initialize.
// The contents of this example are what it would receive it had saved values to the SFMC host during a prior session.
// Most props can be overwritten once the plugin as received this message.
const initActivityMessageExample = (
  organisationId = 'ORG_ID',
  environmentId = 'ENV_ID',
  actionFlowId = 'FLOW_ID'
) => {
  const baseUrl = getApiUrl(CUSTOMER_API_URL)(organisationId)
  const msg = {
    name: '',
    id: v4(),
    key: 'REST-1',
    type: 'REST',
    arguments: {
      executionMode: '{{Context.ExecutionMode}}',
      definitionId: '{{Context.DefinitionId}}',
      activityId: '{{Activity.Id}}',
      contactKey: '{{Context.ContactKey}}',
      execute: {
        inArguments: [
          {
            variables: {
              name: '{{Contact.Attribute.A_DATA_EXTENSION.NAME_FIELD}}' // example of a prior saved value
            },
            schemaVersion: '1',
            targetUserIds: ['{{Contact.Attribute.A_DATA_EXTENSION.ID_FIELD}}']
          }
        ],
        outArguments: [],
        url: `${baseUrl}/${environmentId}/connector/${actionFlowId}/SECRET`,
        useJwt: true,
        verb: 'POST',
        format: 'json'
      },
      testExecute: '',
      startActivityKey: '{{Context.StartActivityKey}}',
      definitionInstanceId: '{{Context.DefinitionInstanceId}}',
      requestObjectId: '{{Context.RequestObjectId}}'
    },
    configurationArguments: {
      save: {
        url: 'https://routing-api.staging.atomic.io/plugin/sfmc-jb/save', // could parameterize this but not much point
        verb: 'POST',
        useJwt: false
      },
      testSave: '',
      publish: {
        url: 'https://routing-api.staging.atomic.io/plugin/sfmc-jb/publish',
        verb: 'POST',
        useJwt: false
      },
      testPublish: '',
      unpublish: '',
      stop: {
        url: 'https://routing-api.staging.atomic.io/plugin/sfmc-jb/stop',
        verb: 'POST',
        useJwt: false
      },
      testStop: '',
      testUnpublish: '',
      partnerActivityId: '',
      validate: {
        url: 'https://routing-api.staging.atomic.io/plugin/sfmc-jb/validate',
        verb: 'POST',
        useJwt: false
      },
      testValidate: '',
      outArgumentSchema: {}
    },
    metaData: {
      icon: 'https://sfmc.plugins.staging.atomic.io/images/icon.png', // could parameterize this but not much point
      iconSmall: 'https://sfmc.plugins.staging.atomic.io/images/iconSmall.png',
      category: 'message',
      atomic_io: {
        organisationId,
        environmentId,
        actionFlowId
      },
      statsContactIcon: '',
      original_icon: 'images/icon.png',
      original_iconSmall: 'images/iconSmall.png',
      isConfigured: true
    },
    schema: {
      arguments: {
        execute: {
          inArguments: [],
          outArguments: []
        }
      }
    },
    editable: true,
    outcomes: [
      {
        key: v4(),
        next: 'WAITBYDURATION-1',
        arguments: {},
        metaData: {
          invalid: false
        }
      }
    ],
    errors: null
  }
  return msg
}

export const MessagingTest = ({
  connection,
  data
}: {
  connection: any
  data: unknown
}) => {
  const [log, setLog] = useState<{event: string; args: unknown[]}[]>([])
  const [visible, setVisible] = usePersistedState(
    false,
    'sfmc-debugger-visible',
    ts.boolean
  )
  const [outgoingState, setOutgoingState] = useStateChanged({
    triggerName: 'updateActivity' as string | undefined,
    payload: {foo: 'bar'} as unknown
  })

  const [incomingState, setIncomingState] = useStateChanged({
    triggerName: 'initActivity' as string | undefined,
    payload: initActivityMessageExample() as unknown
  })

  useEffect(() => {
    events.forEach(event => {
      connection.on(event, (...args: unknown[]) =>
        setLog(c => [{event, args}, ...c])
      )
    })
  }, [connection])

  return (
    <>
      <h4 onClick={() => setVisible(v => !v)} style={{cursor: 'pointer'}}>
        <span
          style={{
            display: 'inline-block',
            transform: visible ? 'rotate(90deg)' : 'none'
          }}
        >
          &gt;
        </span>{' '}
        Debugger
      </h4>

      <div
        style={{
          borderBottom: '1px solid #ccc',
          display: visible ? 'block' : 'none'
        }}
      >
        <pre>{JSON.stringify(data, null, 2)}</pre>
        <div
          style={{
            borderBottom: '1px solid #ccc',
            margin: '0 0 20px',
            padding: '0 0 20px',
            gap: '20px',
            display: 'flex'
          }}
        >
          <form
            style={{flex: 1}}
            onSubmit={e => {
              e.preventDefault()
              connection.trigger(
                outgoingState.triggerName,
                outgoingState.payload
              )
            }}
          >
            <Select
              options={triggers.map(t => ({value: t, text: t}))}
              label="Event name"
              value={outgoingState.triggerName}
              onChange={triggerName =>
                setOutgoingState(c => ({...c, triggerName}))
              }
            />
            <br />
            <JSONField
              value={outgoingState.payload}
              raw={true}
              onChange={payload => setOutgoingState(c => ({...c, payload}))}
              legacyMode={true}
            />
            <br />

            <div style={{textAlign: 'right'}}>
              <Button type="submit">Trigger message to SFMC host</Button>
            </div>
          </form>
          <form
            style={{flex: 1}}
            onSubmit={e => {
              e.preventDefault()
              connection.trigger(
                incomingState.triggerName,
                incomingState.payload
              )
            }}
          >
            <Select
              options={events.map(t => ({value: t, text: t}))}
              label="Event name"
              value={incomingState.triggerName}
              onChange={triggerName =>
                setIncomingState(c => ({...c, triggerName}))
              }
            />
            <br />
            <JSONField
              value={incomingState.payload}
              raw={true}
              onChange={payload => setIncomingState(c => ({...c, payload}))}
              legacyMode={true}
            />
            <br />

            <div style={{textAlign: 'right'}}>
              <Button type="submit">Trigger message to plugin</Button>
            </div>
          </form>
          <div style={{flex: 1}}>
            <h4>Incoming messages</h4>
            <div
              style={{maxHeight: '360px', maxWidth: '400px', overflow: 'auto'}}
            >
              <ul style={{fontSize: '12px', margin: '5px 0', padding: '0'}}>
                {log.length ? (
                  log.map(a => (
                    <li key={Math.random()}>
                      <strong>{a.event}:</strong>
                      <ul
                        style={{
                          fontSize: '10px',
                          margin: '5px 0',
                          padding: '0 0 0 5px'
                        }}
                      >
                        {a.args.map(arg => (
                          <li key={Math.random()}>
                            <pre>{JSON.stringify(arg, null, 2)}</pre>
                          </li>
                        ))}
                      </ul>
                    </li>
                  ))
                ) : (
                  <li>No messages yet</li>
                )}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
